import { makeStyles } from "makeStyles";
import { AppRoleWithAssignees } from "../models";
import PermissionsAssignees from "./PermissionsAssignees";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { permissionsTableStyles } from "../styles";
import { SortableTableHead, getComparator, TableSortOrder, SimpleSortableTableHeadProps } from "../SortableTableHead";
import { useMemo, useState } from "react";
import _ from "lodash";

const useStyles = makeStyles()((theme) => ({
  ...permissionsTableStyles(theme),
  identifierCell: {
    width: "300px"
  },
  displayNameCell: {
    width: "350px"
  },
  descriptionCell: {
    width: "600px"
  }
}));

interface Props {
  permissions?: AppRoleWithAssignees[];
}

const AppAccessPermissions: React.FunctionComponent<Props> = (props) => {
  const { classes } = useStyles();
  const [order, setOrder] = useState<TableSortOrder>("asc");
  const [orderBy, setOrderBy] = useState<keyof AppRoleWithAssignees>("identifier");

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof AppRoleWithAssignees) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const displayRows = useMemo(
    () => [...(props.permissions ?? [])].sort(getComparator(order, orderBy)),
    [order, orderBy, props.permissions]
  );

  function PermissionsTableHead({ id, label, className }: SimpleSortableTableHeadProps<AppRoleWithAssignees>) {
    return (
      <SortableTableHead
        className={className}
        headCell={{
          id,
          label
        }}
        order={order}
        orderBy={orderBy}
        onRequestSort={handleRequestSort}
      />
    );
  }

  return (
    <TableContainer component={Paper} className={classes.topLevelTableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <PermissionsTableHead id="identifier" label="Identifier" className={classes.identifierCell} />
            <PermissionsTableHead id="displayName" label="Display Name" className={classes.displayNameCell} />
            <PermissionsTableHead id="description" label="Description" className={classes.descriptionCell} />
            <TableCell>Assignees</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayRows?.map((permission) => (
            <TableRow key={permission.id}>
              <TableCell>{permission.identifier}</TableCell>
              <TableCell>{permission.displayName}</TableCell>
              <TableCell>{permission.description}</TableCell>
              <TableCell>
                <details>
                  <summary>
                    {permission.assignees?.length} Group{permission.assignees.length > 1 ? "s" : ""} (
                    {(() => {
                      const allMembers = permission.assignees?.flatMap((a) => a.members?.map((m) => m.id) ?? []) ?? [];
                      const uniqueMembers = new Set(allMembers);
                      return uniqueMembers.size + " unique member" + (uniqueMembers.size > 1 ? "s" : "");
                    })()}
                    ):
                  </summary>
                  <PermissionsAssignees assignees={_.orderBy(permission.assignees, [(a) => a.name])} />
                </details>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AppAccessPermissions;
